'use client';

import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import { useQuery } from '@parkchirp/api-client';
import React, { FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { CommonContext } from '../../contexts';
import { GET_HOURLY_ACCOUNT_V2 } from '../../queries';
import { HourlyAccountUserGroup } from '../../types';

type Option = HourlyAccountUserGroup & {
	label: string;
	value: string;
	key: string;
};

const noneOption: Partial<Option> = {
	label: 'None',
	value: 'None',
	key: 'none',
};

interface HourlyAccountRoleAutocompleteProps {
	hourlyAccountId: string;
	hourlyAccountUserGroupId?: string;
	hourlyAccountUserGroupName?: string;
	label?: string;
	disabled?: boolean;
	disableClearable?: boolean;
	error?: boolean;
	required?: boolean;
	onChange?: (value: Option) => void;
	loadingDependency?: boolean;
	size?: 'small' | 'medium';
}

export const HourlyAccountRoleAutocomplete: FC<HourlyAccountRoleAutocompleteProps> = ({
	hourlyAccountId,
	hourlyAccountUserGroupId,
	hourlyAccountUserGroupName,
	label,
	disabled = false,
	disableClearable = false,
	error = false,
	required = false,
	onChange,
	loadingDependency = false,
	size = 'medium',
}) => {
	const { apolloHeaders } = useContext(CommonContext);
	const defaultOption: Partial<Option> = useMemo(() => {
		if (!hourlyAccountId || !hourlyAccountUserGroupId) {
			return noneOption;
		}

		return {
			label: hourlyAccountUserGroupName,
			value: hourlyAccountUserGroupId,
			key: hourlyAccountUserGroupId,
		};
	}, [hourlyAccountId, hourlyAccountUserGroupId, hourlyAccountUserGroupName]);

	const [options, setOptions] = useState<Option[]>([defaultOption as Option]);

	const [open, setOpen] = useState(false);
	const [selectedOption, setSelectedOption] = useState<Option>(defaultOption as Option);

	useEffect(() => {
		setSelectedOption(defaultOption as Option);
	}, [defaultOption, setSelectedOption]);

	const onAutocompleteChange = useCallback(
		(option: Option) => {
			setSelectedOption(option);
			onChange(option);
		},
		[setSelectedOption, onChange],
	);

	const { loading } = useQuery(GET_HOURLY_ACCOUNT_V2, {
		variables: {
			id: hourlyAccountId,
		},
		onCompleted: (data) => {
			const newOptions: Option[] = data.getHourlyAccount.hourlyAccountUserGroups.map(
				(hourlyAccountUserGroup) => ({
					...hourlyAccountUserGroup,
					label: hourlyAccountUserGroup.userGroup.name,
					value: hourlyAccountUserGroup.id,
					key: hourlyAccountUserGroup.id,
				}),
			);
			setOptions(newOptions);
		},
		skip: !hourlyAccountId,
		context: {
			apiVersion: 'v2',
			appName: process.env.NEXT_PUBLIC_APP_NAME,
			appHeaders: apolloHeaders,
		},
	});

	const isLoading = loading || loadingDependency;

	return (
		<Autocomplete
			open={open}
			onOpen={() => {
				setOpen(true);
			}}
			onClose={() => {
				setOpen(false);
			}}
			value={selectedOption}
			onChange={(_, value) => {
				onAutocompleteChange(value);
			}}
			options={options}
			isOptionEqualToValue={(option, { value }) => option.value === value}
			loading={isLoading}
			filterOptions={(x) => x}
			forcePopupIcon={false}
			fullWidth
			renderOption={(props, option) => (
				<li {...props} key={option.key}>
					{option.label}
				</li>
			)}
			renderInput={(params) => (
				<TextField
					{...params}
					label={label || ''}
					required={required}
					error={error}
					InputProps={{
						...params.InputProps,
						endAdornment: (
							<>
								{isLoading ? (
									<CircularProgress color='inherit' size={20} data-cy='loading-indicator' />
								) : null}
								{params.InputProps.endAdornment}
							</>
						),
					}}
				/>
			)}
			disabled={disabled}
			readOnly={disabled}
			disableClearable={disableClearable}
			sx={{
				'& .MuiInputBase-root': {
					paddingRight: '39px !important',
				},
			}}
			size={size}
		/>
	);
};
